import type { APIResponse } from '@/types/api'
import { useAuthStore } from '@/store/authStore'

export class APIClientError extends Error {
  code: string
  status: number
  constructor(code: string, message: string, status: number) {
    super(message)
    this.code = code
    this.status = status
  }
}

function resolveApiBase(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL || (import.meta as any).env?.VITE_API_BASE_URL
  if (envUrl && typeof envUrl === 'string' && envUrl.trim() !== '') {
    return envUrl.trim().replace(/\/$/, '')
  }
  return ''
}

export const API_BASE = resolveApiBase()

let refreshInFlight: Promise<string | null> | null = null

// refreshAccessToken calls POST /api/root/auth/refresh, which relies on the
// httpOnly refresh cookie the browser sends automatically — the refresh
// token itself is never readable by frontend JS.
async function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE}/api/root/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) return null
        const body = (await res.json()) as APIResponse<{ accessToken: string }>
        return body.data?.accessToken ?? null
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  params?: Record<string, string | number | undefined>
  retry?: boolean
}

function buildQuery(params?: Record<string, string | number | undefined>): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, retry = true } = options
  const token = useAuthStore.getState().accessToken

  const targetUrl = path.startsWith('http') ? `${path}${buildQuery(params)}` : `${API_BASE}${path}${buildQuery(params)}`
  const res = await fetch(targetUrl, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  // Access token expired mid-session: refresh once and retry the original
  // call transparently. If refresh also fails, the user is logged out.
  if (res.status === 401 && retry) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      useAuthStore.getState().setAccessToken(newToken)
      return apiRequest<T>(path, { ...options, retry: false })
    }
    useAuthStore.getState().clear()
    throw new APIClientError('UNAUTHORIZED', 'Your session expired. Please log in again.', 401)
  }

  const json = (await res.json().catch(() => null)) as APIResponse<T> | null
  if (!res.ok || !json || !json.success) {
    const err = json?.error
    throw new APIClientError(
      err?.code ?? 'UNKNOWN_ERROR',
      err?.message ?? `Request failed (${res.status})`,
      res.status,
    )
  }
  return json.data as T
}

export async function apiRequestWithMeta<T, M>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: M }> {
  const { method = 'GET', body, params, retry = true } = options
  const token = useAuthStore.getState().accessToken

  const targetUrl = path.startsWith('http') ? `${path}${buildQuery(params)}` : `${API_BASE}${path}${buildQuery(params)}`
  const res = await fetch(targetUrl, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (res.status === 401 && retry) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      useAuthStore.getState().setAccessToken(newToken)
      return apiRequestWithMeta<T, M>(path, { ...options, retry: false })
    }
    useAuthStore.getState().clear()
    throw new APIClientError('UNAUTHORIZED', 'Your session expired. Please log in again.', 401)
  }

  const json = (await res.json().catch(() => null)) as APIResponse<T> | null
  if (!res.ok || !json || !json.success) {
    const err = json?.error
    throw new APIClientError(
      err?.code ?? 'UNKNOWN_ERROR',
      err?.message ?? `Request failed (${res.status})`,
      res.status,
    )
  }
  return { data: json.data as T, meta: json.meta as M }
}
